import React, { Dispatch, SetStateAction, useMemo, useState } from 'react';
import { Button, Modal, Table } from 'tea-component';
import { getPlatformIcons } from '@intelligence/utils/common';
import useIntlExtend from '@intelligence/hooks/useIntlExtend';
import SortBy from '@intelligence/components/TableComponents/components/SortBy';
import { sortReleaseDateData } from '@intelligence/pages/TopCharts/MobileGames/components/GamesDetails/GameInfo/components/ReleaseDate/MoreReleaseDateModal/_utils';
import igdbIcon from '@intelligence/assets/svg/gameInfo/igdb.svg';
import otherPlatformIcon from '@/pages/intelligence/assets/svg/dataSource/other-platform.svg';

import './index.scss';

const { scrollable } = Table.addons;

interface IMoreReleaseDateModal {
  visible: boolean;
  setVisible: Dispatch<SetStateAction<boolean>>;
  data: any[];
  isRelease: boolean;
}

export interface IReleaseDateSortObj {
  key: string;
  sortVal: 'desc' | 'asc';
}

// 默认使用 status 发布阶段状态 phase 降序
const getDefaultSortConfig: (isRelease: boolean) => IReleaseDateSortObj = (isRelease) => ({
  key: 'status',
  sortVal: isRelease ? 'desc' : 'asc',
});

function MoreReleaseDateModal({ visible, setVisible, data, isRelease }: IMoreReleaseDateModal) {
  const { formatMessage } = useIntlExtend();

  const [currentSortObj, setCurrentSortObj] = useState(getDefaultSortConfig(isRelease));

  const handleSortChange = (sortObj: any) => {
    setCurrentSortObj(sortObj);
  };

  const releaseType = useMemo(() => {
    if (!isRelease) {
      return 'not_release';
    }

    const isOffline = data?.find((item) => item.originStatus?.toLowerCase() === 'offline');
    if (isOffline) {
      return 'offline';
    }

    return 'released';
  }, [isRelease, data]);

  const sortedData = useMemo(() => {
    const res = sortReleaseDateData(data, currentSortObj, releaseType);
    return res;
  }, [data, currentSortObj, releaseType]);

  const columns = [
    {
      key: 'platform',
      title: formatMessage({ id: 'dbi_platform' }),
      width: 225,
      header: (column: any) => {
        return (
          <SortBy
            align={'left'}
            title={column.title}
            onChange={handleSortChange}
            defaultKey={currentSortObj.key}
            defaultValue={currentSortObj.sortVal}
            list={[
              {
                key: 'platform',
                text: column.title,
                title: column.title,
              },
            ]}
          />
        );
      },
      render(column: any) {
        const isMacOrLinux = ['linux'].includes(column?.platform?.toLowerCase());
        return (
          <div className="dbi-release-date-show-more-modal-platform">
            <img
              src={getPlatformIcons(isMacOrLinux ? 'pc' : column.platform) || otherPlatformIcon}
            ></img>
            {column.platform}
          </div>
        );
      },
    },
    {
      key: 'country',
      title: formatMessage({ id: 'dbi_release_region' }),
      header: (column: any) => {
        return (
          <SortBy
            align={'left'}
            title={column.title}
            onChange={handleSortChange}
            defaultKey={currentSortObj.key}
            defaultValue={currentSortObj.sortVal}
            list={[
              {
                key: 'country',
                text: column.title,
                title: column.title,
              },
            ]}
          />
        );
      },
    },
    {
      key: 'status',
      title: formatMessage({ id: 'dbi_release_phase' }),
      header: (column: any) => {
        return (
          <SortBy
            align={'left'}
            title={column.title}
            onChange={handleSortChange}
            defaultKey={currentSortObj.key}
            defaultValue={currentSortObj.sortVal}
            list={[
              {
                key: 'status',
                text: column.title,
                title: column.title,
              },
            ]}
          />
        );
      },
    },
    {
      key: 'release_date',
      title: formatMessage({ id: 'dbi_release_date_new' }),
      header: (column: any) => {
        return (
          <SortBy
            align={'left'}
            title={column.title}
            onChange={handleSortChange}
            defaultKey={currentSortObj.key}
            defaultValue={currentSortObj.sortVal}
            list={[
              {
                key: 'release_date',
                text: column.title,
                title: column.title,
              },
            ]}
          />
        );
      },
      render(data: { release_date: string }) {
        return data?.release_date || 'TBD';
      },
    },
  ];

  const handleClose = () => {
    setVisible?.(false);
  };

  return (
    <Modal
      visible={visible}
      size="l"
      onClose={handleClose}
      caption={
        <div className="dbi-release-date-show-more-modal-title">
          <img className="dbi-release-date-title-img" src={igdbIcon}></img>
          {formatMessage({ id: 'dbi_release_date_new' })}
        </div>
      }
    >
      <div className="dbi-release-date-show-more-modal-content">
        <Table
          columns={columns}
          records={sortedData}
          addons={[
            scrollable({
              maxHeight: 507,
            }),
          ]}
        />
      </div>

      <div className="dbi-release-date-show-more-modal-footer">
        <Button type="primary" onClick={handleClose}>
          {formatMessage({ id: 'dbi_close' })}
        </Button>
      </div>
    </Modal>
  );
}

export default MoreReleaseDateModal;
